export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private audioChunks: Blob[] = [];

  constructor(
    private onDataAvailable: (audioData: string) => void,
    private onRecordingStart?: () => void,
    private onRecordingStop?: () => void,
    private onError?: (error: Error) => void
  ) {}

  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/wav'
    ];
    return types.find(type => MediaRecorder.isTypeSupported(type)) || '';
  }

  async startRecording(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      const mimeType = this.getSupportedMimeType();
      const options = mimeType ? { mimeType } : {};
      
      this.mediaRecorder = new MediaRecorder(this.stream, options);

      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = async () => {
        const mimeType = this.getSupportedMimeType() || 'audio/webm';
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });
        const base64Audio = await this.blobToBase64(audioBlob);
        this.onDataAvailable(base64Audio);
        this.onRecordingStop?.();
      };

      this.mediaRecorder.onerror = (event) => {
        const error = new Error(`Recording error: ${event}`);
        this.onError?.(error);
      };

      this.mediaRecorder.start();
      this.onRecordingStart?.();
    } catch (error) {
      const recordingError = new Error(`Failed to start recording: ${error}`);
      this.onError?.(recordingError);
      throw recordingError;
    }
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  }

  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix (e.g., "data:audio/webm;base64,")
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }
}

export const checkMicrophonePermission = async (): Promise<boolean> => {
  try {
    const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return result.state === 'granted';
  } catch {
    // Fallback: try to get user media to check permission
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch {
      return false;
    }
  }
};